import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { CrmBoardService } from '../services/crm-board.service';
import { CrmColumnService } from '../services/crm-column.service';
import { BoardDetailResponse, BoardSummaryResponse } from '../services/responses/board.response';
import { apiErrorMessage } from '@functions/api-error-message';

interface Params {
    board: BoardDetailResponse | null;
    summaries: BoardSummaryResponse[];
    selectedBoardId: string;
    setSelectedBoardId: (id: string) => void;
    setBoard: (board: BoardDetailResponse | null) => void;
    refreshBoard: (boardId: string) => Promise<void>;
    refetchBoards: () => Promise<void>;
}

export const useCrmBoardActions = ({
    board,
    summaries,
    selectedBoardId,
    setSelectedBoardId,
    setBoard,
    refreshBoard,
    refetchBoards,
}: Params) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert, alertDelete } = useAlert();

    const columnService = new CrmColumnService();
    const boardService = new CrmBoardService();

    const doDeleteColumn = async (columnId: string) => {
        setLoading(dispatch, true);
        try {
            await columnService.deleteById(columnId);
            if (selectedBoardId) await refreshBoard(selectedBoardId);
            showAlert(t('general.exclusaoSucesso'), 'success', 2000);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    const handleDeleteColumn = (columnId: string) => {
        const col = board?.columns.find((c) => c.id === columnId);
        if (col && col.leads.length > 0) {
            showAlert(t('crmKanban.colunaComLeads'), 'error', 4000);
            return;
        }
        alertDelete(columnId, doDeleteColumn, col?.name);
    };

    const doDeleteBoard = async (id: string) => {
        setLoading(dispatch, true);
        try {
            await boardService.deleteById(id);
            await refetchBoards();
            if (id === selectedBoardId) {
                const nextId = summaries.find((s) => s.id !== id)?.id ?? '';
                setSelectedBoardId(nextId);
                if (!nextId) setBoard(null);
            }
            showAlert(t('general.exclusaoSucesso'), 'success', 2000);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    const handleDeleteBoard = (id: string) => {
        const summary = summaries.find((s) => s.id === id);
        if (summary && summary.leadCount > 0) {
            showAlert(t('crmKanban.quadroComLeads'), 'error', 4000);
            return;
        }
        alertDelete(id, doDeleteBoard, summary?.name);
    };

    const handleToggleBoardActive = async () => {
        if (!board) return;

        const closing = board.active;
        setLoading(dispatch, true);
        try {
            await boardService.update(board.id, {
                name: board.name,
                active: !closing,
                isDefault: board.isDefault,
                isIntake: board.isIntake,
            });
            await refetchBoards();
            await refreshBoard(board.id);
            showAlert(
                closing ? t('crmKanban.quadroFechadoSucesso') : t('crmKanban.quadroReabertoSucesso'),
                'success',
                2000
            );
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    return { handleDeleteColumn, handleDeleteBoard, handleToggleBoardActive };
};
